import { useAppTheme } from "@hooks/useAppTheme";
import type {
	WorkflowItemEdge,
	WorkflowItemNode,
} from "@memorang/types/src/session/new-session";
import {
	Background,
	type Connection,
	MarkerType,
	type OnConnect,
	type OnEdgesChange,
	type OnNodesChange,
	ReactFlow,
	ReactFlowProvider,
} from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomEdge from "./CustomEdge";
import {
	DecisionNode,
	DefaultNode,
	InputNode,
	OutputNode,
} from "./StandardNodes";
import useWorkflowStore from "./useWorkflowStore";
import { compareWorkflowGraphs, createWorkflowReport } from "./utils";

import "@xyflow/react/dist/style.css";

const nodeTypes = {
	"input-node": InputNode,
	"default-node": DefaultNode,
	"output-node": OutputNode,
	"decision-node": DecisionNode,
};

const edgeTypes = {
	custom: CustomEdge,
};

const nodeOrigin: [number, number] = [0.5, 0];

const WorkflowGraphCore = ({
	initialNodes,
	initialEdges,
	correctNodes,
	correctEdges,
	handleUpdateResponse,
	disabled,
	mode,
}: {
	initialNodes: WorkflowItemNode[];
	initialEdges: WorkflowItemEdge[];
	correctNodes: WorkflowItemNode[];
	correctEdges: WorkflowItemEdge[];
	handleUpdateResponse: (workflowReport: string) => void;
	disabled?: boolean;
	mode: "user" | "correct";
}) => {
	const theme = useAppTheme();
	const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
	const [isReconnecting, setIsReconnecting] = useState(false);

	const nodes = useWorkflowStore((state) => state.nodes);
	const edges = useWorkflowStore((state) => state.edges);
	const setNodes = useWorkflowStore((state) => state.setNodes);
	const setEdges = useWorkflowStore((state) => state.setEdges);
	const onNodesChange = useWorkflowStore((state) => state.onNodesChange);
	const onEdgesChange = useWorkflowStore((state) => state.onEdgesChange);
	const onConnect = useWorkflowStore((state) => state.onConnect);
	const onReconnect = useWorkflowStore((state) => state.onReconnect);
	const addEdge = useWorkflowStore((state) => state.addEdge);
	const isModified = useRef(false);

	const updateWorkflowReport = useCallback(() => {
		if (disabled) {
			return;
		}
		const workflowReport = createWorkflowReport(correctNodes, correctEdges);
		handleUpdateResponse(workflowReport);
	}, [correctNodes, correctEdges, handleUpdateResponse, disabled]);

	// Initialize the graph with the initial nodes and edges
	useEffect(() => {
		if (nodes.length === 0) {
			setNodes(initialNodes);
			setEdges(initialEdges);
		}
	}, [initialNodes, initialEdges, setNodes, setEdges, nodes]);

	const { nodes: fixedNodes, edges: fixedEdges } = useMemo(() => {
		if (!disabled) {
			return {
				nodes: nodes,
				edges: edges,
			};
		}
		return compareWorkflowGraphs(correctNodes, correctEdges, nodes, edges);
	}, [correctNodes, correctEdges, nodes, edges, disabled]);

	// Create edges with updatable property based on selection and correctness
	const edgesWithUpdatable = useMemo(() => {
		const edgesToUse =
			mode === "correct" ? correctEdges : disabled ? fixedEdges : edges;
		return edgesToUse.map((edge) => {
			const selected = edge.id === selectedEdgeId;

			let strokeColor = selected ? theme.colors.primary : undefined;
			let markerColor = selected
				? theme.colors.primary
				: theme.colors.outlineVariant;

			if (disabled && mode !== "correct") {
				strokeColor = edge.data?.correct ? strokeColor : theme.colors.error;
				markerColor = edge.data?.correct ? markerColor : theme.colors.error;
			}

			return {
				...edge,
				selected,
				style: {
					...edge.style,
					stroke: strokeColor,
					strokeWidth: selected ? 2 : undefined,
				},
				markerEnd: {
					type: MarkerType.ArrowClosed,
					width: selected ? 10 : 20,
					height: selected ? 10 : 20,
					color: markerColor,
				},
				// Only selected edge is updatable when one is selected
				updatable: selectedEdgeId ? selected : true,
				reconnectable: selectedEdgeId ? selected : true,
			};
		});
	}, [
		edges,
		selectedEdgeId,
		correctEdges,
		mode,
		theme.colors.primary,
		theme.colors.outlineVariant,
		theme.colors.error,
		disabled,
		fixedEdges,
	]);

	const onEdgeClick = useCallback(
		(event: React.MouseEvent, edge: WorkflowItemEdge) => {
			event.preventDefault();
			event.stopPropagation();
			setSelectedEdgeId(edge.id === selectedEdgeId ? null : edge.id);
		},
		[selectedEdgeId],
	);

	const onPaneClick = useCallback(() => {
		if (!isReconnecting) {
			setSelectedEdgeId(null);
		}
	}, [isReconnecting]);

	const onReconnectStart = useCallback(() => {
		setIsReconnecting(true);
	}, []);

	const onReconnectEnd = useCallback(() => {
		setIsReconnecting(false);
		setSelectedEdgeId(null);
	}, []);

	const handleNodesChange: OnNodesChange<WorkflowItemNode> = useCallback(
		(changes) => {
			onNodesChange(changes);
			// because of the useEffect, we don't want to submit the first time
			if (!isModified.current) {
				isModified.current = true;
				return;
			}
			updateWorkflowReport();
		},
		[onNodesChange, updateWorkflowReport],
	);

	const handleEdgesChange: OnEdgesChange = useCallback(
		(changes) => {
			onEdgesChange(changes);
			updateWorkflowReport();
		},
		[onEdgesChange, updateWorkflowReport],
	);

	const handleReconnect = useCallback(
		(oldEdge: WorkflowItemEdge, newConnection: Connection) => {
			onReconnect(oldEdge, newConnection);
			updateWorkflowReport();
		},
		[onReconnect, updateWorkflowReport],
	);

	const handleOnConnect: OnConnect = useCallback(
		(connection) => {
			if (!connection.sourceHandle) {
				onConnect(connection);
				updateWorkflowReport();
				return;
			}
			const newEdge: WorkflowItemEdge = {
				...connection,
				id: `${connection.source}-${connection.target}-${connection.sourceHandle}`,
				label: "",
				type: "custom",
			};
			addEdge(newEdge);
			updateWorkflowReport();
		},
		[onConnect, addEdge, updateWorkflowReport],
	);

	return (
		<div style={{ flexGrow: 1, height: "100%" }}>
			<ReactFlow
				nodes={
					mode === "correct" ? correctNodes : disabled ? fixedNodes : nodes
				}
				edges={edgesWithUpdatable}
				onNodesChange={handleNodesChange}
				onEdgesChange={handleEdgesChange}
				onConnect={handleOnConnect}
				onReconnect={handleReconnect}
				onReconnectStart={onReconnectStart}
				onReconnectEnd={onReconnectEnd}
				onEdgeClick={onEdgeClick}
				onPaneClick={onPaneClick}
				preventScrolling={false}
				zoomOnScroll={false}
				nodeTypes={nodeTypes}
				edgeTypes={edgeTypes}
				fitView
				nodesConnectable={!disabled}
				edgesReconnectable={!disabled}
				nodesDraggable={!disabled}
				nodeOrigin={nodeOrigin}
				proOptions={{
					hideAttribution: true,
				}}
				defaultEdgeOptions={{
					type: "smoothstep",
					interactionWidth: 20,
					markerEnd: {
						type: MarkerType.ArrowClosed,
						width: 20,
						height: 20,
					},
				}}
			>
				<Background />
			</ReactFlow>
		</div>
	);
};

export const WorkflowGraph = ({
	initialNodes,
	initialEdges,
	correctNodes,
	correctEdges,
	handleUpdateResponse,
	disabled,
	mode = "user",
}: {
	initialNodes: WorkflowItemNode[];
	initialEdges: WorkflowItemEdge[];
	correctNodes: WorkflowItemNode[];
	correctEdges: WorkflowItemEdge[];
	handleUpdateResponse: (workflowReport: string) => void;
	disabled?: boolean;
	mode?: "user" | "correct";
}) => {
	return (
		<ReactFlowProvider>
			<WorkflowGraphCore
				initialNodes={initialNodes}
				initialEdges={initialEdges}
				correctNodes={correctNodes}
				correctEdges={correctEdges}
				handleUpdateResponse={handleUpdateResponse}
				disabled={disabled}
				mode={mode}
			/>
		</ReactFlowProvider>
	);
};
